import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text } from '@datanomik/core-ui';
import { ArrowRightUpLine, Sparkling2Line } from '@datanomik/core-ui/icons';

const HACKATHON_URL =
  'https://datanomik.com/hackathon?utm_source=dashboard&utm_medium=login_banner&utm_campaign=hackathon_ia_tesouraria';
const REGISTRATION_DEADLINE = new Date(2026, 10, 2, 23, 59, 59);
const DAY_MS = 24 * 60 * 60 * 1000;
const PAIN_ROTATE_MS = 2600;

const PAIN_KEYS = [
  'hackathon_pain_reconciliation',
  'hackathon_pain_forecast',
  'hackathon_pain_collections',
  'hackathon_pain_payments',
  'hackathon_pain_reports',
];

const FORMAT_KEYS = ['hackathon_format_individual', 'hackathon_format_online', 'hackathon_format_free'];

const SPARKS = [
  { top: '14%', left: '82%', size: 'size-4', delay: '0s' },
  { top: '58%', left: '90%', size: 'size-3', delay: '0.9s' },
  { top: '30%', left: '64%', size: 'size-2.5', delay: '1.7s' },
  { top: '78%', left: '70%', size: 'size-3.5', delay: '2.3s' },
];

const ORBIT_LIGHT: CSSProperties = {
  background:
    'conic-gradient(from 0deg, transparent 0deg, var(--color-chart-2) 70deg, var(--color-chart-5) 130deg, transparent 190deg, transparent 250deg, var(--color-brand-foreground) 320deg, transparent 360deg)',
};

const TITLE_GRADIENT: CSSProperties = {
  backgroundImage:
    'linear-gradient(90deg, var(--color-brand-foreground) 0%, var(--color-chart-5) 35%, var(--color-chart-2) 50%, var(--color-brand-foreground) 70%)',
  backgroundSize: '200% auto',
};

export function isHackathonRegistrationOpen(now: Date) {
  return now <= REGISTRATION_DEADLINE;
}

function daysUntilDeadline(now: Date) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const deadlineDay = new Date(
    REGISTRATION_DEADLINE.getFullYear(),
    REGISTRATION_DEADLINE.getMonth(),
    REGISTRATION_DEADLINE.getDate(),
  );
  return Math.round((deadlineDay.getTime() - today.getTime()) / DAY_MS);
}

function daysLeftLabelKey(days: number) {
  if (days === 0) return 'hackathon_last_day';
  return days === 1 ? 'hackathon_day_left' : 'hackathon_days_left';
}

function useRotatingIndex(length: number, intervalMs: number) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setIndex((current) => (current + 1) % length), intervalMs);
    return () => clearInterval(timer);
  }, [length, intervalMs]);

  return index;
}

function RotatingPain() {
  const { t } = useTranslation();
  const index = useRotatingIndex(PAIN_KEYS.length, PAIN_ROTATE_MS);

  return (
    <div className="flex items-center gap-2 overflow-hidden rounded-lg border border-white/10 bg-black/25 px-3 py-2 font-mono text-[13px] text-brand-foreground/80">
      <span className="text-chart-3">›</span>
      <span className="text-brand-foreground/55">{t('hackathon_terminal_call')}(</span>
      <span key={index} className="truncate text-chart-5 motion-safe:animate-fade-in-up-fast">
        &quot;{t(PAIN_KEYS[index]!)}&quot;
      </span>
      <span className="text-brand-foreground/55">)</span>
      <span aria-hidden="true" className="h-4 w-2 shrink-0 bg-brand-foreground/70 motion-safe:animate-caret" />
    </div>
  );
}

export function HackathonBanner() {
  const { t } = useTranslation();
  const now = new Date();

  if (!isHackathonRegistrationOpen(now)) return null;

  const daysLeft = daysUntilDeadline(now);

  return (
    <section
      aria-labelledby="hackathon-banner-title"
      className="relative max-w-xl overflow-hidden rounded-2xl p-px shadow-2xl shadow-black/40 motion-safe:animate-rise-in"
    >
      <div
        aria-hidden="true"
        className="absolute left-1/2 top-1/2 aspect-square w-[160%] -translate-x-1/2 -translate-y-1/2 motion-safe:animate-orbit"
        style={ORBIT_LIGHT}
      />

      <div className="relative flex flex-col gap-5 overflow-hidden rounded-[15px] bg-brand-deep/95 p-6 backdrop-blur-xl">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <span className="absolute -right-16 -top-20 size-56 rounded-full bg-chart-2/25 blur-3xl motion-safe:animate-drift" />
          <span
            className="absolute -bottom-24 -left-10 size-64 rounded-full bg-chart-7/40 blur-3xl motion-safe:animate-drift"
            style={{ animationDelay: '-5s' }}
          />
          {SPARKS.map((spark) => (
            <Sparkling2Line
              key={`${spark.top}-${spark.left}`}
              className={`absolute ${spark.size} text-brand-foreground/80 motion-safe:animate-twinkle`}
              style={{ top: spark.top, left: spark.left, animationDelay: spark.delay }}
            />
          ))}
        </div>

        <div className="relative flex flex-wrap items-center justify-between gap-3">
          <span className="inline-flex items-center gap-2 rounded-full border border-chart-3/40 bg-chart-3/15 px-3 py-1">
            <span className="size-2 rounded-full bg-chart-3 motion-safe:animate-status-dot" />
            <Text variant="caption" weight="semibold" className="text-brand-foreground">
              {t('hackathon_registration_open')}
            </Text>
          </span>
        </div>

        <div className="relative flex flex-col gap-2">
          <Text
            id="hackathon-banner-title"
            variant="title"
            as="h3"
            className="bg-clip-text text-transparent motion-safe:animate-shimmer-text"
            style={TITLE_GRADIENT}
          >
            {t('hackathon_title')}
          </Text>
          <Text variant="body" className="text-brand-foreground/75">
            {t('hackathon_tagline')}
          </Text>
        </div>

        <div className="relative">
          <RotatingPain />
        </div>

        <ul className="relative m-0 flex list-none flex-wrap gap-2 p-0">
          {FORMAT_KEYS.map((key) => (
            <li
              key={key}
              className="rounded-full border border-white/15 bg-white/[0.06] px-3 py-1 text-xs font-medium text-brand-foreground/85"
            >
              {t(key)}
            </li>
          ))}
        </ul>

        <div className="relative flex flex-wrap items-end justify-between gap-4 border-t border-white/10 pt-5">
          <div className="flex items-baseline gap-2">
            {daysLeft > 0 && (
              <span className="text-4xl font-bold tabular-nums leading-none text-brand-foreground">
                {daysLeft}
              </span>
            )}
            <div className="flex flex-col">
              <Text variant="body-sm" weight="semibold" className="text-brand-foreground/90">
                {t(daysLeftLabelKey(daysLeft))}
              </Text>
              <Text variant="caption" className="text-brand-foreground/55">
                {t('hackathon_deadline')}
              </Text>
            </div>
          </div>

          <Button asChild className="relative overflow-hidden">
            <a href={HACKATHON_URL} target="_blank" rel="noopener noreferrer">
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 left-0 w-1/2 -skew-x-12 bg-gradient-to-r from-transparent via-white/35 to-transparent motion-safe:animate-sheen-loop"
              />
              {t('hackathon_cta')}
              <ArrowRightUpLine aria-hidden="true" />
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}
